import { PacingBlockV2 } from 'shared/src/api/line-item-types';
import type { ISODateRange, ISODateString } from 'shared/src/date-string-utils/types';
import {
  addDays,
  daysBetween,
  endOfMonth,
  isAfter,
  isAfterOrEqual,
  isWithinRange,
  startOfMonth,
  today as getToday
} from 'shared/src/date-string-utils/date-utils';
import { round2 } from 'shared/src/utils/number/number-utils';
import { PacingValidatorSpend } from 'shared/src/domain/line-item/pacing-validator';

/**
 * Generates monthly pacing blocks distributed by weighted month allocation.
 *
 * Partial months are prorated by (campaign days in month / calendar days in month).
 * The last block gets the remainder so the budget total is exact.
 */
export function generateMonthlyBlocks(dates: ISODateRange, budget: number): PacingBlockV2[] {
  const months = monthRanges(dates);
  if (months.length === 0) return [];

  const totalWeight = months.reduce((sum, m) => sum + m.weight, 0);
  let spentSoFar = 0;

  return months.map((m, idx) => {
    const isLast = idx === months.length - 1;
    const blockBudget = isLast
      ? round2(budget - spentSoFar)
      : round2((m.weight / totalWeight) * budget);
    spentSoFar = round2(spentSoFar + blockBudget);
    return { dates: { from: m.from, to: m.to }, budget: blockBudget };
  });
}

/**
 * Proportionally rescale pacing blocks so their budgets sum to `newTotal` (e.g. after a price
 * change), preserving each block's dates and the block count. The last block absorbs the rounding
 * remainder so the total is exact to two decimals. When `oldTotal` is non-positive (can't scale
 * proportionally) the full new total lands on the last block.
 */
export function rescalePacingToTotal<T extends PacingBlockV2>(
  blocks: T[],
  oldTotal: number,
  newTotal: number
): T[] {
  if (blocks.length === 0) return blocks;
  const scalable = oldTotal > 0;
  let spent = 0;
  return blocks.map((block, idx) => {
    const isLast = idx === blocks.length - 1;
    const budget = isLast
      ? round2(newTotal - spent)
      : scalable
        ? round2((block.budget / oldTotal) * newTotal)
        : 0;
    spent = round2(spent + budget);
    return { ...block, budget };
  });
}

type SplitInput<T extends PacingBlockV2> = {
  pacing_type: 'lifetime' | 'custom';
  pacing_blocks: T[];
  dates: ISODateRange;
  old_price: number;
  price: number;
  dailyBudgetSpent: PacingValidatorSpend;
};

export function splitCurrentPacingBlock<T extends PacingBlockV2>(input: SplitInput<T>): T[] {
  return input.pacing_type === 'lifetime' ? splitLifetime(input) : splitCustom(input);
}

export function cumulativeSpend(
  dates: ISODateRange,
  blocks: PacingBlockV2[]
): { day: ISODateString; cumulativeTotal: number }[] {
  const numDays = daysBetween(dates.from, dates.to);
  const result: { day: ISODateString; cumulativeTotal: number }[] = [];
  let runningTotal = 0;

  for (let i = 0; i < numDays; i++) {
    const day = addDays(dates.from, i);
    runningTotal += dailySpendOn(day, blocks);
    result.push({ day, cumulativeTotal: runningTotal });
  }
  return result;
}

export function isStartOnOrBeforeToday(lineItemDates: ISODateRange, today: ISODateString): boolean {
  return !isAfter(lineItemDates.from, today);
}

function monthRanges(
  dates: ISODateRange
): { from: ISODateString; to: ISODateString; weight: number }[] {
  const { from, to } = dates;
  if (isAfter(from, to)) {
    throw new Error("Invalid range: 'to' is before 'from'.");
  }

  const ranges: { from: ISODateString; to: ISODateString; weight: number }[] = [];
  let cursor = from;
  while (!isAfter(cursor, to)) {
    const monthEnd = endOfMonth(cursor);
    const blockTo = isAfter(monthEnd, to) ? to : monthEnd;
    const campaignDays = daysBetween(cursor, blockTo);
    const calendarDays = daysBetween(startOfMonth(cursor), endOfMonth(cursor));
    ranges.push({ from: cursor, to: blockTo, weight: campaignDays / calendarDays });
    cursor = addDays(blockTo, 1);
  }
  return ranges;
}

function splitLifetime<T extends PacingBlockV2>(input: SplitInput<T>): T[] {
  const { dates, old_price, price, dailyBudgetSpent, pacing_blocks } = input;
  const now = getToday();
  if (isAfterOrEqual(now, dates.to)) return pacing_blocks;
  const totalDays = daysBetween(dates.from, dates.to);
  const pastDays = daysBetween(dates.from, now);
  const proRataOldPrice = (pastDays / totalDays) * old_price;
  const totalBillable = sumDailySpend(dailyBudgetSpent);
  // Past block keeps at least its historical share (and the billable revenue floor when the
  // new price allows it). When new price < that floor, collapse the past block to the full
  // new price so totals always sum to `price`. The validator surfaces a warning when the
  // past block falls below billable revenue — no hard block.
  const pastBudget = Math.min(Math.max(proRataOldPrice, totalBillable), price);
  const futureBudget = price - pastBudget;
  const source = pacing_blocks[0];

  return [
    cloneBlock(source, { from: dates.from, to: now }, round2(pastBudget)),
    pendingBlock<T>({ from: addDays(now, 1), to: dates.to }, round2(futureBudget))
  ];
}

function splitCustom<T extends PacingBlockV2>(input: SplitInput<T>): T[] {
  const now = getToday();
  return input.pacing_blocks.flatMap(b => splitOneBlock(b, now));
}

function splitOneBlock<T extends PacingBlockV2>(block: T, today: ISODateString): T[] {
  // Past or future block — keep as-is.
  if (!isWithinRange(today, block.dates)) return [block];

  if (isAfterOrEqual(today, block.dates.to)) return [block];

  // Block contains today — split at today.
  const blockDays = daysBetween(block.dates.from, block.dates.to);
  const pastDays = daysBetween(block.dates.from, today);
  const pastBudget = (pastDays / blockDays) * block.budget;
  return [
    { ...block, dates: { from: block.dates.from, to: today }, budget: round2(pastBudget) },
    pendingBlock<T>(
      { from: addDays(today, 1), to: block.dates.to },
      round2(block.budget - pastBudget)
    )
  ];
}

function cloneBlock<T extends PacingBlockV2>(
  source: T | undefined,
  dates: ISODateRange,
  budget: number
): T {
  return source ? { ...source, dates, budget } : (pendingBlock<T>(dates, budget) as T);
}

function pendingBlock<T extends PacingBlockV2>(dates: ISODateRange, budget: number): T {
  return { dates, budget, origin: 'pending' } as unknown as T;
}

function sumDailySpend(rows: PacingValidatorSpend): number {
  return rows.reduce((sum, row) => sum + (row.amount ?? 0), 0);
}

function dailySpendOn(day: ISODateString, blocks: PacingBlockV2[]): number {
  return blocks.reduce(
    (sum, block) => sum + (isWithinRange(day, block.dates) ? blockDailyRate(block) : 0),
    0
  );
}

function blockDailyRate(block: PacingBlockV2): number {
  return block.budget / daysBetween(block.dates.from, block.dates.to);
}
